/**
 * Fix DT (2026-09-22): Ingeniería → Informes. "Deberíamos tener un espacio donde
 * podamos hacer informes… que la IA nos ayude a redactar y que salga con el
 * membrete nuestro" (Edson, 21/09).
 *
 * El técnico carga los datos en bruto, la IA los ordena en las secciones del tipo
 * de informe elegido, el texto queda editable y el PDF sale con el encabezado, el
 * logo, los colores y el pie de CEN.
 */
import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { FileText, Plus, Sparkles, Save, Download, Trash2, Loader2, X, ArrowLeft } from "lucide-react";
import { API, useAuth } from "../../contexts/AuthContext";
import { toast } from "sonner";

const VACIO = {
  tipo: "tecnico", titulo: "", obra_id: "", obra_nombre: "", cliente: "",
  destinatario: "", fecha: new Date().toISOString().slice(0, 10), elaborado_por: "",
  notas: "", secciones: [],
};

export default function Informes() {
  const { user } = useAuth();

  const [tipos, setTipos] = useState([]);
  const [informes, setInformes] = useState([]);
  const [obras, setObras] = useState([]);
  const [loading, setLoading] = useState(true);
  const [redactando, setRedactando] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [editando, setEditando] = useState(null);   // null = listado, "nuevo" o el id
  const [form, setForm] = useState(VACIO);

  const seccionesDelTipo = useCallback((tipo) => {
    const t = tipos.find(x => x.valor === tipo);
    return (t?.secciones || []).map(s => ({ titulo: s, texto: "" }));
  }, [tipos]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [tps, lst] = await Promise.all([
        axios.get(`${API}/informes/tipos`, { withCredentials: true }),
        axios.get(`${API}/informes`, { withCredentials: true }),
      ]);
      setTipos(Array.isArray(tps.data) ? tps.data : []);
      setInformes(Array.isArray(lst.data) ? lst.data : []);
    } catch { toast.error("Error al cargar los informes"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    axios.get(`${API}/obras`, { withCredentials: true })
      .then(({ data }) => setObras(Array.isArray(data) ? data : [])).catch(() => {});
  }, []);

  const nuevo = () => {
    setForm({ ...VACIO, elaborado_por: user?.name || "", secciones: seccionesDelTipo("tecnico") });
    setEditando("nuevo");
  };

  const abrir = async (inf) => {
    try {
      const { data } = await axios.get(`${API}/informes/${inf._id || inf.id}`, { withCredentials: true });
      setForm({ ...VACIO, ...data, secciones: data.secciones || [] });
      setEditando(data._id || data.id);
    } catch { toast.error("No se pudo abrir el informe"); }
  };

  const cambiarTipo = (tipo) => {
    const vacias = form.secciones.every(s => !(s.texto || "").trim());
    setForm(f => ({ ...f, tipo, secciones: vacias ? seccionesDelTipo(tipo) : f.secciones }));
  };

  const elegirObra = (obra_id) => {
    const o = obras.find(x => (x.id || x._id) === obra_id);
    setForm(f => ({ ...f, obra_id, obra_nombre: o?.nombre || "", cliente: o?.cliente_nombre || f.cliente }));
  };

  const redactar = async () => {
    if ((form.notas || "").trim().length < 20) {
      toast.error("Escribí primero los datos del informe");
      return;
    }
    setRedactando(true);
    try {
      const { data } = await axios.post(`${API}/informes/redactar`, {
        tipo: form.tipo, titulo: form.titulo, obra_nombre: form.obra_nombre, notas: form.notas,
      }, { withCredentials: true });
      setForm(f => ({ ...f, secciones: data?.secciones || f.secciones }));
      toast.success("Borrador redactado: revisalo y corregí lo que haga falta");
    } catch (e) { toast.error(e?.response?.data?.detail || "La IA no pudo redactar el informe"); }
    finally { setRedactando(false); }
  };

  const guardar = async () => {
    if (!(form.titulo || "").trim()) { toast.error("Poné un título al informe"); return null; }
    setGuardando(true);
    try {
      const cuerpo = { ...form, secciones: form.secciones };
      const { data } = editando === "nuevo"
        ? await axios.post(`${API}/informes`, cuerpo, { withCredentials: true })
        : await axios.put(`${API}/informes/${editando}`, cuerpo, { withCredentials: true });
      toast.success("Informe guardado");
      setEditando(data._id || data.id);
      setForm(f => ({ ...f, ...data }));
      load();
      return data._id || data.id;
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo guardar"); return null; }
    finally { setGuardando(false); }
  };

  const descargarPdf = async () => {
    const id = editando === "nuevo" ? await guardar() : editando;
    if (!id) return;
    try {
      const res = await axios.get(`${API}/informes/${id}/pdf`, { withCredentials: true, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${form.titulo || "Informe"}.pdf`;
      link.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("No se pudo generar el PDF"); }
  };

  const borrar = async (inf) => {
    try {
      await axios.delete(`${API}/informes/${inf._id || inf.id}`, { withCredentials: true });
      toast.success("Informe borrado");
      load();
    } catch { toast.error("No se pudo borrar"); }
  };

  const setSeccion = (i, campo, valor) => {
    setForm(f => ({ ...f, secciones: f.secciones.map((s, k) => k === i ? { ...s, [campo]: valor } : s) }));
  };
  const agregarSeccion = () => setForm(f => ({ ...f, secciones: [...f.secciones, { titulo: "", texto: "" }] }));
  const quitarSeccion = (i) => setForm(f => ({ ...f, secciones: f.secciones.filter((_, k) => k !== i) }));

  // ── Listado ────────────────────────────────────────────────────────────────
  if (!editando) {
    return (
      <div className="p-4 sm:p-6 lg:p-8 space-y-6 min-h-full" data-testid="informes-page">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 tracking-tight flex items-center gap-2">
              <FileText size={26} className="text-[#8B5CF6]" /> Informes
            </h1>
            <p className="text-sm text-gray-500 mt-0.5">Informes técnicos, de avance, de visita y comunicados, con el membrete de CEN</p>
          </div>
          <button onClick={nuevo} className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-lg bg-[#8B5CF6] text-white hover:bg-[#7C3AED]"
            data-testid="informes-nuevo">
            <Plus size={14} /> Nuevo informe
          </button>
        </div>

        <div className="bg-white rounded-2xl border border-gray-200/60 shadow-sm overflow-hidden">
          <table className="w-full" data-testid="informes-tabla">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50/50">
                {["N°", "Título", "Tipo", "Obra", "Fecha", "Elaborado por", ""].map(h => (
                  <th key={h} className="text-left px-3 py-2.5 text-[10px] font-bold text-gray-400 uppercase tracking-wide">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} className="text-center py-12 text-gray-400 text-sm">
                  <Loader2 size={20} className="mx-auto mb-2 animate-spin" /> Cargando…
                </td></tr>
              ) : informes.length === 0 ? (
                <tr><td colSpan={7} className="text-center py-12 text-gray-400 text-sm">Todavía no hay informes</td></tr>
              ) : informes.map(inf => (
                <tr key={inf._id || inf.id} className="border-b border-gray-50 hover:bg-gray-50/40 cursor-pointer"
                  data-testid={`informe-fila-${inf._id || inf.id}`} onClick={() => abrir(inf)}>
                  <td className="px-3 py-2.5 text-xs font-mono text-gray-500">{inf.numero}</td>
                  <td className="px-3 py-2.5 text-sm font-semibold text-gray-900">{inf.titulo}</td>
                  <td className="px-3 py-2.5 text-xs text-gray-500">
                    {(tipos.find(t => t.valor === inf.tipo) || {}).label || inf.tipo}
                  </td>
                  <td className="px-3 py-2.5 text-xs text-gray-500">{inf.obra_nombre || "—"}</td>
                  <td className="px-3 py-2.5 text-xs text-gray-500">{inf.fecha}</td>
                  <td className="px-3 py-2.5 text-xs text-gray-500">{inf.elaborado_por || "—"}</td>
                  <td className="px-3 py-2.5 text-right">
                    <button onClick={e => { e.stopPropagation(); borrar(inf); }}
                      className="text-gray-400 hover:text-rose-600" data-testid={`informe-borrar-${inf._id || inf.id}`}>
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  // ── Editor ─────────────────────────────────────────────────────────────────
  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-5 min-h-full" data-testid="informe-editor">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <button onClick={() => { setEditando(null); load(); }}
          className="flex items-center gap-1.5 text-xs font-semibold text-gray-500 hover:text-gray-800"
          data-testid="informe-volver"><ArrowLeft size={14} /> Volver a los informes</button>
        <div className="flex items-center gap-2">
          <button onClick={guardar} disabled={guardando}
            className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-50"
            data-testid="informe-guardar">
            {guardando ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Guardar
          </button>
          <button onClick={descargarPdf}
            className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-lg bg-[#2E6DA4] text-white hover:bg-[#245A8A]"
            data-testid="informe-pdf">
            <Download size={14} /> Descargar PDF
          </button>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-gray-200/60 shadow-sm p-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
        <label className="text-xs font-semibold text-gray-500 sm:col-span-2">Título del informe
          <input value={form.titulo} onChange={e => setForm(f => ({ ...f, titulo: e.target.value }))}
            placeholder="Acometidas y distribución en BT — Obra Palmanova"
            className="block border border-gray-200 rounded-lg px-2 py-1.5 text-sm w-full mt-1" data-testid="informe-titulo" />
        </label>
        <label className="text-xs font-semibold text-gray-500">Tipo
          <select value={form.tipo} onChange={e => cambiarTipo(e.target.value)}
            className="block border border-gray-200 rounded-lg px-2 py-1.5 text-sm w-full mt-1" data-testid="informe-tipo">
            {tipos.map(t => <option key={t.valor} value={t.valor}>{t.label}</option>)}
          </select>
        </label>
        <label className="text-xs font-semibold text-gray-500">Obra
          <select value={form.obra_id} onChange={e => elegirObra(e.target.value)}
            className="block border border-gray-200 rounded-lg px-2 py-1.5 text-sm w-full mt-1" data-testid="informe-obra">
            <option value="">Sin obra</option>
            {obras.map(o => <option key={o.id || o._id} value={o.id || o._id}>{o.nombre}</option>)}
          </select>
        </label>
        <label className="text-xs font-semibold text-gray-500">Destinatario
          <input value={form.destinatario} onChange={e => setForm(f => ({ ...f, destinatario: e.target.value }))}
            className="block border border-gray-200 rounded-lg px-2 py-1.5 text-sm w-full mt-1" data-testid="informe-destinatario" />
        </label>
        <label className="text-xs font-semibold text-gray-500">Fecha
          <input type="date" value={(form.fecha || "").slice(0, 10)} onChange={e => setForm(f => ({ ...f, fecha: e.target.value }))}
            className="block border border-gray-200 rounded-lg px-2 py-1.5 text-sm w-full mt-1" data-testid="informe-fecha" />
        </label>
      </div>

      <div className="bg-white rounded-2xl border border-gray-200/60 shadow-sm p-4 space-y-3">
        <div className="flex items-center justify-between gap-3">
          <div>
            <p className="text-sm font-semibold text-gray-900">Datos del informe</p>
            <p className="text-xs text-gray-500">Escribí lo que viste y lo que se hizo, como te salga. La IA lo ordena en las secciones del informe.</p>
          </div>
          <button onClick={redactar} disabled={redactando}
            className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-lg bg-[#8B5CF6] text-white hover:bg-[#7C3AED] disabled:opacity-50 whitespace-nowrap"
            data-testid="informe-redactar">
            {redactando ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />} Redactar con IA
          </button>
        </div>
        <textarea value={form.notas} onChange={e => setForm(f => ({ ...f, notas: e.target.value }))} rows={6}
          placeholder="Ej.: se relevó el tablero general, faltan los bornes de la fase R, el cliente pidió adelantar la acometida…"
          className="border border-gray-200 rounded-lg px-3 py-2 text-sm w-full" data-testid="informe-notas" />
      </div>

      <div className="space-y-3">
        {form.secciones.map((s, i) => (
          <div key={i} className="bg-white rounded-2xl border border-gray-200/60 shadow-sm p-4 space-y-2"
            data-testid={`informe-seccion-${i}`}>
            <div className="flex items-center gap-2">
              <span className="text-xs font-bold text-gray-400">{i + 1}.</span>
              <input value={s.titulo} onChange={e => setSeccion(i, "titulo", e.target.value)}
                placeholder="Título de la sección"
                className="flex-1 border-0 border-b border-gray-200 px-1 py-1 text-sm font-semibold focus:outline-none focus:border-[#8B5CF6]"
                data-testid={`informe-seccion-titulo-${i}`} />
              <button onClick={() => quitarSeccion(i)} className="text-gray-300 hover:text-rose-600"
                data-testid={`informe-seccion-quitar-${i}`}><X size={14} /></button>
            </div>
            <textarea value={s.texto} onChange={e => setSeccion(i, "texto", e.target.value)} rows={4}
              className="border border-gray-200 rounded-lg px-3 py-2 text-sm w-full"
              data-testid={`informe-seccion-texto-${i}`} />
          </div>
        ))}
        <button onClick={agregarSeccion}
          className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-lg border border-dashed border-gray-300 text-gray-500 hover:bg-gray-50"
          data-testid="informe-agregar-seccion"><Plus size={14} /> Agregar sección</button>
      </div>
    </div>
  );
}
